import React, { createContext, useContext } from 'react'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { LoadingWithSpinner } from 'modules/finance/components/FinancialWorkspace/components/LoadingWithSpinner'
import { FinancialWorkspaceAvailabilities } from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceAvailabilities'
import { useSeePayrollLanding } from 'modules/finance/components/FinancialWorkspacePayroll/hooks/useSeePayrollLanding'

import { usePermissions } from '../../hooks/usePermissions'

interface Props {
  children: React.ReactNode
}

export interface Availabilities extends FinancialWorkspaceAvailabilities {
  seePayrollLanding: boolean
  canSeeLaborCosts: boolean
  canSeeCompensations: boolean
  canSeePayroll: boolean
}

interface IValue {
  availabilities: Availabilities
  loading: boolean
}

const AvailabilitiesProviderContext = createContext<IValue | undefined>(undefined)

export const AvailabilitiesProvider: React.FC<Props> = ({ children }) => {
  const { availabilities } = useFinancialWorkspace()
  const { seePayrollLanding, loading: loadingSeePayrollLanding } = useSeePayrollLanding()
  const { permissions } = usePermissions()

  const canSeeCompensations = permissions.canSeeCompensations
  const canSeePayroll = permissions.canSeePayroll
  const canSeeLaborCosts = canSeeCompensations || canSeePayroll

  if (loadingSeePayrollLanding || availabilities.loading) {
    return <LoadingWithSpinner />
  }

  return (
    <AvailabilitiesProviderContext.Provider
      value={{
        availabilities: {
          seePayrollLanding,
          canSeeLaborCosts,
          canSeeCompensations,
          canSeePayroll,
          ...availabilities,
        },
        loading: !!loadingSeePayrollLanding || availabilities.loading,
      }}
    >
      {children}
    </AvailabilitiesProviderContext.Provider>
  )
}

export const useAvailabilities = () => {
  const context = useContext(AvailabilitiesProviderContext)
  if (context === undefined) {
    throw new Error('useAvailabilities must be used within a AvailabilitiesProvider')
  }
  return context
}
